import React from "react";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";

export interface TokenQuota {
  available: number;
  used: number;
}

/** % de tokens encore libres (disponible / total). */
function freePercent(available: number, used: number): number {
  const total = Math.max(0, available) + Math.max(0, used);
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, (available / total) * 100));
}

function ringColor(pct: number): "success" | "warning" | "error" {
  if (pct > 40) return "success";
  if (pct > 15) return "warning";
  return "error";
}

type Props = {
  quota: TokenQuota | null;
  loading?: boolean;
  size?: number;
};

/**
 * Anneau avec % de tokens libres uniquement (aligné mobile).
 */
const TokenQuotaRing: React.FC<Props> = ({
  quota,
  loading = false,
  size = 36,
}) => {
  const available = quota?.available ?? 0;
  const used = quota?.used ?? 0;
  const pct = freePercent(available, used);
  const color = ringColor(pct);
  const thickness = 3;
  const label = `${Math.round(pct)}%`;

  return (
    <Tooltip
      title={
        loading
          ? "Chargement du quota…"
          : `Tokens libres : ${label}`
      }
      arrow
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          userSelect: "none",
        }}
        aria-label={`Tokens libres : ${label}`}
      >
        <Box sx={{ position: "relative", width: size, height: size }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={size}
            thickness={thickness}
            sx={{
              color: "action.hover",
              position: "absolute",
              left: 0,
              top: 0,
            }}
          />
          <CircularProgress
            variant="determinate"
            value={loading ? 0 : pct}
            size={size}
            thickness={thickness}
            color={color}
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              [`& .MuiCircularProgress-circle`]: {
                strokeLinecap: "round",
                transition: "stroke-dashoffset 0.4s ease",
              },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {loading ? (
              <CircularProgress size={12} thickness={5} />
            ) : (
              <Typography
                component="span"
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  lineHeight: 1,
                  color: "text.primary",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {label}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default TokenQuotaRing;
